import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  text: string;
  children: React.ReactNode;
  position?: "top" | "bottom";
  /** Texto longo com quebra de linha e largura máxima */
  wide?: boolean;
  align?: "start" | "center" | "end";
}

const Tooltip: React.FC<TooltipProps> = ({
  text,
  children,
  position = "bottom",
  wide = false,
  align = "center",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 8;
    const padding = 8;
    const tw = tooltip?.offsetWidth ?? (wide ? 320 : 0);
    const th = tooltip?.offsetHeight ?? (wide ? 80 : 28);

    let left =
      align === "end"
        ? rect.right - tw
        : align === "start"
          ? rect.left
          : rect.left + rect.width / 2 - tw / 2;

    left = Math.max(padding, Math.min(left, window.innerWidth - tw - padding));

    const top =
      position === "top" ? rect.top - th - gap : rect.bottom + gap;

    setCoords({ top, left });
  }, [align, position, wide]);

  useEffect(() => {
    if (!isVisible) return;

    const reposition = () => {
      requestAnimationFrame(() => updatePosition());
    };

    reposition();
    const onScrollOrResize = () => reposition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isVisible, text, updatePosition]);

  const show = () => {
    setIsVisible(true);
  };

  const hide = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </span>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            role="tooltip"
            className={`pointer-events-none fixed z-[9999] rounded-md bg-neutral-800 px-3 py-2.5 text-xs leading-relaxed text-white shadow-lg ${
              wide
                ? "max-w-sm whitespace-normal text-left"
                : "whitespace-nowrap"
            }`}
            style={{ top: coords.top, left: coords.left }}
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  );
};

export default Tooltip;
